
import { ref, computed, defineComponent, watch, nextTick, toRef, onMounted } from "vue";

import { darkMode } from '../globals';

import InteractiveText from "./InteractiveText.vue";
declare const FATE: any;
export default defineComponent({
  name: "Console",
  components: {
    InteractiveText,
  },
  props: ["init", "gameState"],
  setup(props, context) {
    let scrollTo: "top" | "bottom" = null;
    let title = ref("");
    let text = ref("");
    let buffer = ref<string>("");
    let bufferInput = ref();
    let textConsole = ref();
    let wrapper = ref();
    let verbs = ref<string[]>([]);
    let validateBuffer = false;
    let oldScrollHeight = 0;

    let currentAutocompleteIndex = 0;
    let autocompleteMatches = [];
    let autocompleteTerm = '';

    let doubleClickTimeout;
    let firstClick = false;

    let clickListenerAdded = false;

    onMounted(() => {
      if (clickListenerAdded) return;
      wrapper.value.addEventListener("click", (event: Event) => {
        context.emit('click');
        if (firstClick) {
          event.preventDefault();
          event.stopPropagation();
          firstClick = false;
          enterCommand();
        } else {
          firstClick = true;
          setTimeout(() => firstClick = false, 300);
        }
      }, {capture: true});
      clickListenerAdded = true;
    });

    window.addEventListener("keydown", (event) => {
      if (event.key === 'Tab') {
        event.preventDefault();
        performAutocomplete();
        return;
      }

      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        textConsole?.value?.focus();
        return;
      }

      validateBuffer = true;

      autocompleteMatches = [];
      currentAutocompleteIndex = 0;
      autocompleteTerm = '';

      if (document.activeElement === bufferInput.value) {
        return;
      }

      bufferInput?.value?.focus();
    });
    
    function performAutocomplete() {
        const currentBuffer = buffer.value;

        if (!currentBuffer || currentBuffer[currentBuffer.length -1] === ' ') {
          return;
        }

        const term = currentBuffer.split(' ').pop().toLowerCase();

        if (!autocompleteTerm) {
          autocompleteTerm = term;
        }

        let match;

        if (autocompleteMatches.length && currentAutocompleteIndex < autocompleteMatches.length) {
          match = autocompleteMatches[currentAutocompleteIndex++];
        } 

        if (!match) {
          const verbsValue = verbs.value;
          for (let i = 0; i < verbsValue.length; i++) {
            const lowerCase = verbsValue[i].toLowerCase();
            if (lowerCase === term) {
              break;
            }

            if (lowerCase.startsWith(autocompleteTerm)) {
              match = lowerCase;
              break;
            }
          }
        }

        if (!match) {
          autocompleteMatches = [];
          currentAutocompleteIndex = 0;
          const regex = new RegExp(`\\b\\W*(${autocompleteTerm}.*?)\\W*\\b`, 'ig');
          let regexMatch = regex.exec(text.value);
          while (regexMatch) {
            if (!autocompleteMatches.includes(regexMatch[1])) {
              autocompleteMatches.push(regexMatch[1]);
            }
            regexMatch = regex.exec(text.value);
          }

          if (autocompleteMatches.length) {
            match = autocompleteMatches[currentAutocompleteIndex++];
          }
        }
        
        if (match) {
          buffer.value = [...currentBuffer.split(' ').slice(0, -1), match].join(' ');
        }
        
        return;
    }
    
    function loadText(
      source:
        | string
        | {
            header?: string;
            content?: string;
            clear?: boolean;
            scrollTo?: "top" | "bottom";
          }
        | any
    ) {
      if (!source) {
        if (!buffer.value) { return; }
        const bufferText = buffer.value;
        const command = bufferText.toLowerCase().trim();
        
        if (validateBuffer) {
          const splitCommand = command.split(' ');
          for (let i = 0; i < splitCommand.length; i++) {
            const wordRegex = new RegExp(`\\b\\W*${splitCommand[i]}\\W*\\b`, 'i');
            if (!wordRegex.exec(verbs.value.join(' ')) && !wordRegex.exec(text.value)) {
              clearBuffer();
              return;
            }
          }
        }

        source = FATE.enterCommand(command, `> ${bufferText}`);
      }
      const textConsoleElement = document.querySelector("#text-console") as HTMLElement;

      oldScrollHeight = textConsoleElement.scrollHeight;

      if (typeof source === "string") {
        text.value = source;
        scrollTo = null;
      } else {
        if (source.header) {
          title.value = source.header;
        }

        if (source.content) {
          if (source.clear && !source.scrollTo) {
            source.scrollTo = "top";
          }

          text.value = source.content;
        }

        if (source.scrollTo) {
          scrollTo = source.scrollTo;
        } else {
          scrollTo = null;
        }
      }

      verbs.value = FATE.verbs;
    }

    function clearBuffer() {
      validateBuffer = false;
      buffer.value = "";
    }

    function enterCommand(command = null) {
      if (!FATE.gameLoaded) return;

      loadText(command);
      clearBuffer();
    }
    const initRef = toRef(props, 'init');
    watch(initRef, (val) => {
        if (!val) return;

        loadText(val);
    })

    const gameStateRef = toRef(props, 'gameState');
    watch(gameStateRef, (val) => {
        if (!val) return;

        const loaded = FATE.loadState(props.gameState);
        if (!loaded) {
          window.alert("This save file is not for this FATE game!");
          return;
        }
        nextTick(() => {
          loadText(loaded);
          window.alert("Save game loaded!");
        });
    });

    watch(text, () => {
      nextTick(() => {
        const textConsoleElement = document.querySelector("#text-console") as HTMLElement;
        if (scrollTo === "top") {
          textConsoleElement.scrollTop = 0;
        } else if (scrollTo === "bottom") {
          textConsoleElement.scrollTop = textConsoleElement.scrollHeight;
        } else {
          textConsoleElement.scrollTop = oldScrollHeight - 100;
        }
      });
    });

    return {
      title,
      text,
      buffer,
      bufferInput,
      textConsole,
      wrapper,
      verbs,
      //inventory,
      addToBuffer(token: string) {
        const sanitized = token.replace(/[^\w\s]/g, "");
        buffer.value = buffer.value ? buffer.value + " " + sanitized : sanitized;
      },
      enterCommand,
      clearBuffer,

      darkMode
    };
  },
});
